import { Button, Stack, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { Navigate, useNavigate } from "react-router-dom";
import { useAcceptMarketDialogueInviteMutation, useGetMarketDialogueInviteQuery } from "../../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../../common/auth/useLoggedInWithOrgContextState";
import { useApiError } from "../../../common/errors/useApiError";
import { urls } from "../../../urls";
import {
  isInviteGone,
  isInviteRateLimited,
  isWrongInviteLink,
  MarketDialogueInviteFacts,
} from "../MarketDialogueInviteFacts";

interface Props {
  inviteId: string;
  secret: string;
}

export const AcceptMarketDialogueInvite = ({ inviteId, secret }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { trigger } = useApiError();
  const organizationName = useLoggedInWithOrgContextState().selectedOrg.name;
  const { data, error, refetch, isFetching } = useGetMarketDialogueInviteQuery({ inviteId, secret });
  const [accept, { isLoading }] = useAcceptMarketDialogueInviteMutation();

  const onAccept = async () => {
    try {
      const accepted = await accept({ inviteId, secret }).unwrap();
      navigate(urls.app.supplier.marketDialogue.go(accepted.marketDialogueId), { replace: true });
    } catch (e) {
      trigger(e);
    }
  };

  if (error) {
    return <InviteFailure error={error} onRetry={() => void refetch()} isRetrying={isFetching} />;
  }
  if (!data) return <Text>{t("Loading")}...</Text>;
  if (data.accepted) return <Navigate to={urls.app.supplier.marketDialogue.go(data.marketDialogueId)} replace />;

  return (
    <Stack spacing="5">
      <MarketDialogueInviteFacts invite={data} />
      <Button colorScheme="teal" onClick={() => void onAccept()} isLoading={isLoading}>
        {t("Take part as {{organization}}", { organization: organizationName })}
      </Button>
      <Text fontSize="sm" color="smMuted">
        {t("The invitation then belongs to {{organization}}, and everyone there can answer.", {
          organization: organizationName,
        })}
      </Text>
    </Stack>
  );
};

const InviteFailure = ({ error, onRetry, isRetrying }: { error: unknown; onRetry: () => void; isRetrying: boolean }) => {
  const { t } = useTranslation();
  const [title, advice] = isInviteRateLimited(error)
    ? [t("Too many attempts"), t("Wait a few minutes, then open the link from your email again.")]
    : isInviteGone(error)
      ? [t("The market dialogue no longer exists"), t("The contracting authority has removed it, so there is nothing to answer.")]
      : isWrongInviteLink(error)
        ? [
            t("This invitation link is not valid"),
            t("Open the most recent link from your email, or ask the contracting authority to invite you again."),
          ]
        : [t("We could not fetch the invitation"), null];
  return (
    <Stack spacing="4">
      <Text fontSize="2xl" fontWeight="bold">
        {title}
      </Text>
      {advice ? (
        <Text>{advice}</Text>
      ) : (
        <Button colorScheme="teal" onClick={onRetry} isLoading={isRetrying}>
          {t("Try again")}
        </Button>
      )}
    </Stack>
  );
};
